import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { MeetingListDto } from 'src/api/FiretrolApiClient';
import { formatDate, formatDateTime } from 'src/utils';
import { useJobs } from 'src/hooks/useJobs';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import {
  useTable,
  IUseTableProps,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

export interface IMeetingTableProps {
  meetings: MeetingListDto[];
  caption?: string;
  selectedMeetingId?: string | null;
  isLoading?: boolean;
  onMeetingClicked?: (meeting: MeetingListDto) => void;
  tableOptions?: Partial<MRT_TableOptions<MeetingListDto>>;
  tableProps?: Partial<IUseTableProps<MeetingListDto>>;
}

const getColumns = (): MRT_ColumnDef<MeetingListDto>[] => {
  return [
    {
      accessorKey: 'title',
      header: 'Title',
      accessorFn: (row) => (!row.title ? 'Untitled' : row.title),
      size: 250,
    },
    {
      accessorKey: 'meetingType.name',
      header: 'Meeting Type',
      accessorFn: (row) => row.meetingType?.name ?? '-',
      size: 150,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
    },
    {
      accessorKey: 'createdBy.fullName',
      header: 'Created By',
      accessorFn: (row) => row.createdBy?.fullName ?? '',
      size: 150,
    },
    {
      accessorKey: 'startDate',
      header: 'Date',
      accessorFn: (row) => (row.startDate ? formatDate(row.startDate) : ''),
      size: 100,
    },
    {
      accessorKey: 'startTime',
      header: 'Start',
      accessorFn: (row) =>
        row.startDate ? formatDateTime(row.startDate, 'h:mm a') : '',
      size: 80,
    },
    {
      accessorKey: 'endTime',
      header: 'End',
      accessorFn: (row) =>
        row.endDate ? formatDateTime(row.endDate, 'h:mm a') : '',
      size: 80,
    },
  ];
};

export const MeetingTable: React.FC<IMeetingTableProps> = ({
  meetings,
  selectedMeetingId,
  isLoading = false,
  onMeetingClicked,
  tableOptions,
  tableProps,
}) => {
  const { selectedJob } = useJobs();
  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'meetings-table',
    columns: columns,
    defaultSorting: [{ id: 'startDate', desc: true }],
  });

  const tableExports = useTableExports<MeetingListDto>('meetings', (data) => {
    return data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['Title']: item.title,
      ['Date']: item.startDate ? formatDate(item.startDate) : '',
      ['Start']: item.startDate ? formatDateTime(item.startDate, 'h:mm a') : '',
      ['End']: item.endDate ? formatDateTime(item.endDate, 'h:mm a') : '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    ...tableProps,
    tableOptions: {
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const meeting = row.original;
        return (
          <>
            <EditActionIcon onClick={() => onMeetingClicked?.(meeting)} />
          </>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: meetings,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      ...tableOptions,
    },
    getRowId: (row) => String(row.id),
    totalCount: meetings.length,
    isLoading: isLoading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return table;
};
